/* eslint-disable max-lines */
import { DeleteOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT } from '@salescore/client-base'
import { ClosableResizableSiderWithPercent, useCanForView } from '@salescore/client-common'
import { selectCurrentCachedView, useAppSelector } from '@salescore/client-redux'
import {
  type CoreModel,
  getMetricFromKpiFormula,
  type NodePropertyName,
  type ViewConfigFilterNode,
  type ViewConfigKpiMeasure,
  type ViewConfigSheet,
  ViewConfigSheetValueObject,
  type ViewConfigTreeNode,
  type ViewKpiAppearance,
} from '@salescore/core'
import { ButtonWithTooltip } from '@salescore/frontend-common'
import { Alert, Button, Col, Form, Row, Space } from 'antd'
import { Array, Option, pipe } from 'effect'
import { t } from 'i18next'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'

import { DurationTimerEnd } from '../../../components/misc/DurationTimer'
import { KpiFormChildNodeFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormChildNodeFormItem'
import { KpiFormDashboardIdsFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormDashboardIdsFormItem'
import { KpiFormDateFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormDateFormItem'
import { KpiFormDescriptionFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormDescriptionFormItem'
import { KpiFormDetailFormItems } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormDetailFormItems'
import { KpiFormExtraFilterTreeFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormExtraFilterTreeFormItem'
import { KpiFormFilterTreeFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormFilterTreeFormItem'
import { KpiFormIconFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormIconFormItem'
import { KpiFormMeasureFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormMeasureFormItem'
import { KpiFormNameFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormNameFormItem'
import { KpiFormPreview } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormPreview'
import { KpiFormRootNodeFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormRootNodeFormItem'
import { KpiFormSaveButton, useSaveKpiForm } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormSaveButton'
import { KpiFormSheetFieldsFormAddDimeisions } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormSheetFieldsFormAddDimensions'
import { KpiFormSheetFieldsFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormSheetFieldsFormItem'
import { KpiFormSteps } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormSteps'
import { KpiFormUserFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormUserFormItem'
import { KpiFormViewGroupIdFormItem } from '../../../components/view_ui/ViewUIKpiPivot/KpiForm/KpiFormViewGroupIdFormItem'
import type { ReferenceToPropertyForAddChildNodeMutation } from '../../../components/view_ui/ViewUISheet/findSameReferenceToChild'
import {
  kpiFormAtom,
  kpiFormModifiedAtom,
  type SalescoreObjectAbility,
  type ViewAbility,
  viewsContextAtom,
} from '../../../recoil/view/atoms'
import {
  type KpiFormStep,
  MAX_STEP,
  userModels,
  useSetSheetConfig,
  useStepState,
  useStepStatus,
} from '../../../recoil/view/hooks/kpiForm'
import { generateInitialKpiForm } from '../../../recoil/view/mutations/initializeViewMutation'
import { useCurrentSheet, useKpi } from '../../../recoil/view/selectors/kpiFormSelector'
import { useModelSearcher } from '../../../recoil/view/selectors/modelSearcherSelector'
import { updateMetricFormValue } from '../../../recoil/view/util'
import { VisualizeOnlySection } from './VisualizeOnlySection'

function useLatestSheetConfig(viewId: string) {
  const { views } = useRecoilValue(viewsContextAtom)
  const latestConfig = views.find((x) => x.id === viewId)?.config
  // viewsContext は ingestion 境界で 1 回 lift 済みのため、formula.metric は atom 内で参照安定。
  // config 参照が変わったときだけ sheet を取り直し、effect の再実行条件を latestConfig に揃える。
  const latestSheetConfig = useMemo(
    () => (latestConfig?.type === 'kpi' ? getMetricFromKpiFormula(latestConfig.formula)?.sheet : undefined),
    [latestConfig],
  )

  return { latestSheetConfig }
}

export const KpiForm = ({
  view,
  ability,
}: {
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const setSheetConfig = useSetSheetConfig()
  const currentCachedView = useAppSelector((state) => selectCurrentCachedView(state, { viewId: view.id }))
  const { latestSheetConfig } = useLatestSheetConfig(view.id)
  const { loading, saveKpiForm } = useSaveKpiForm({ view })
  // Ctrl+S ハンドラから次 render の saveKpiForm を発火させるためのトークン。
  // FormItem は再レンダリングコスト回避のため onBlur で kpiFormAtom にコミットしており、
  // Ctrl+S 時点では未コミット値が残る。blur → 次 render で最新 atom を読んで保存する。
  // kpiFormAtom をローカルステート化できれば onChange コミットが可能になり、再レンダリングコストを考慮が不要になり、saveToken は不要になる。
  const [saveToken, setSaveToken] = useState(0)

  useEffect(() => {
    // KPIシートパネルで設定が変更されていた場合などに不整合が起きないよう、常に最新のKPIシートの設定をフォームに反映する
    if (latestSheetConfig !== undefined && currentCachedView?.isModifiedKpiForm !== true) {
      setSheetConfig(latestSheetConfig)
    }
  }, [latestSheetConfig])

  // 依存は saveToken のみ。saveKpiForm は毎 render 新規生成のため、
  // 依存に含めると無限ループする（最新クロージャを掴みたい本設計の要）。
  useEffect(() => {
    if (saveToken === 0) return
    void saveKpiForm()
  }, [saveToken])

  return (
    <div
      tabIndex={0} // divでonKeyDownをキャッチするために必要
      className="h-full"
      onKeyDown={(event) => {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
          const active = document.activeElement
          if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
            // 保存後にSelectが展開するのを防ぐためにフォーカスを外す
            active.blur()
          }
          setSaveToken((x) => x + 1)
          // ブラウザのデフォルトの保存ダイアログを表示させないようにする
          event.preventDefault()
        }
      }}
    >
      <Body
        view={view}
        ability={ability}
        kpiSaveing={loading.isTrue}
        kpiSave={() => {
          void saveKpiForm()
        }}
      />
    </div>
  )
}

const Body = ({
  view,
  ability,
  kpiSaveing,
  kpiSave,
}: {
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
  kpiSaveing: boolean
  kpiSave: () => void
}): ReactElement => {
  // eslint-disable-next-line sonarjs/todo-tag
  const canUpdate = useCanForView('update', view) // TODO: KPIフォームの各種のFormItemについてもdisableをするべきだが、実装がやや大変なので一旦保留
  const [currentStep, setCurrentStep] = useStepState(0)

  return (
    <ClosableResizableSiderWithPercent
      siderKey="KpiForm"
      showMinimum
      widthPercent={40}
      position="right"
      sider={({ close }) => (
        <div className="flex size-full flex-col">
          <div
            className="flex items-center justify-between px-8 py-2 align-middle"
            style={{ borderBottom: `1px solid ${CONSTANT.colors.border}` }}
          >
            <div className="text-lg font-bold">{t(`KPIのプレビュー`)}</div>
            <Button type="text" onClick={close}>
              {t(`閉じる`)}
            </Button>
          </div>
          <div className="min-h-0 flex-1">
            <KpiFormPreview view={view} ability={ability} />
          </div>
        </div>
      )}
      body={() => (
        <div className="h-full overflow-y-auto p-6">
          {!canUpdate && <Alert className="mb-4" showIcon type="warning" message={t(`権限がないため編集できません`)} />}
          <KpiFormSteps currentStep={currentStep} setCurrentStep={setCurrentStep} />
          <Form
            initialValues={{
              name: view.name,
            }}
            // wrapperCol={{ span: 8 }}
            onKeyDown={(event: React.KeyboardEvent<HTMLFormElement>) => {
              // enter キーで submit しない（textareaのEnterは無効化しない）
              if (event.key === 'Enter' && !(event.target instanceof HTMLTextAreaElement)) {
                event.preventDefault()
              }
            }}
            // 本来であればonFinishを使いたいところだが、ロジックをSaveButtonコンポーネントに寄せることを意識して、ここでは使わない
          >
            <VisualizeOnlySection>
              <>
                <StepContent0
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  kpiSaveing={kpiSaveing}
                  kpiSave={kpiSave}
                  view={view}
                />
                <StepContent1
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  kpiSaveing={kpiSaveing}
                  kpiSave={kpiSave}
                  view={view}
                />
                <StepContent2
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  kpiSaveing={kpiSaveing}
                  kpiSave={kpiSave}
                  view={view}
                />
                <StepContent3
                  currentStep={currentStep}
                  setCurrentStep={setCurrentStep}
                  kpiSaveing={kpiSaveing}
                  kpiSave={kpiSave}
                  view={view}
                />
              </>
            </VisualizeOnlySection>
          </Form>
          <DurationTimerEnd view={view} />
        </div>
      )}
    />
  )
}

const StepContent0 = ({
  currentStep,
  setCurrentStep,
  kpiSaveing,
  kpiSave,
  view,
}: {
  currentStep: KpiFormStep
  setCurrentStep: (value: number) => void
  /** KPIの保存ローディング状態（ショートカットキーでの保存状態を共有する）*/
  kpiSaveing: boolean
  /** KPIの保存実行 */
  kpiSave: () => void
  view: ViewFieldsFragment
}) => {
  const [form, setForm] = useRecoilState(kpiFormAtom)
  const { kpi, setKpi } = useKpi()
  const setFormModified = useSetRecoilState(kpiFormModifiedAtom)

  const [name, setName] = useState(form.name)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setName(form.name)
  }, [form.name])

  const [description, setDescription] = useState(kpi.description ?? '')
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDescription(kpi.description ?? '')
  }, [kpi.description])

  const onChangeName = (name: string): void => {
    setName(name)
    setFormModified(true)
  }

  const onChangeDescription = (description: string): void => {
    setDescription(description)
    setFormModified(true)
  }

  const onChangeIcon = (icon: string): void => {
    setKpi({ icon })
    setFormModified(true)
  }

  const onChangeViewGroupId = (viewGroupId: string | null): void => {
    setForm((oldValue) => ({ ...oldValue, viewGroupId }))
    setFormModified(true)
  }

  const setDashboardIds = (dashboardIds: string[]): void => {
    setForm((oldValue) => ({ ...oldValue, dashboardIds }))
    // setFormModified(true)はKpiFormDashboardIdsFormItemで行っている
  }

  return (
    <StepContentWrapper
      targetStep={0}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      kpiSaveing={kpiSaveing}
      kpiSave={kpiSave}
      view={view}
    >
      <KpiFormNameFormItem name={name} setName={onChangeName} />
      <KpiFormDescriptionFormItem description={description} setDescription={onChangeDescription} />
      <KpiFormIconFormItem icon={kpi.icon} onChangeIcon={onChangeIcon} />
      <KpiFormViewGroupIdFormItem
        view={view}
        viewGroupId={form.viewGroupId === undefined ? view.viewGroupId : form.viewGroupId}
        onChangeViewGroupId={onChangeViewGroupId}
      />
      <KpiFormDashboardIdsFormItem
        view={view}
        dashboardIds={form.dashboardIds ?? []}
        setDashboardIds={setDashboardIds}
      />
    </StepContentWrapper>
  )
}

const StepContent1 = ({
  currentStep,
  setCurrentStep,
  kpiSaveing,
  kpiSave,
  view,
}: {
  currentStep: KpiFormStep
  setCurrentStep: (value: number) => void
  /** KPIの保存ローディング状態（ショートカットキーでの保存状態を共有する）*/
  kpiSaveing: boolean
  /** KPIの保存実行 */
  kpiSave: () => void
  view: ViewFieldsFragment
}) => {
  const [currentSheet, setCurrentSheet] = [useCurrentSheet(), useSetSheetConfig()]
  const setForm = useSetRecoilState(kpiFormAtom)
  const setFormModified = useSetRecoilState(kpiFormModifiedAtom)
  const sheet = new ViewConfigSheetValueObject(currentSheet)
  const modelSearcher = useModelSearcher()

  const onChangeModel = (model: CoreModel): void => {
    const tree: ViewConfigTreeNode = {
      type: 'model',
      name: model.name,
      modelName: model.name,
    }
    const sheet = new ViewConfigSheetValueObject({
      type: 'sheet',
      tree,
    })

    const sheetWithDefaults = sheet.addDefaultPropertyFields(tree.name, model)

    const userProperty = pipe(
      model.properties,
      Array.findFirst((property) => property.referenceTo?.some((x) => userModels.has(x.modelName)) === true),
      Option.map((userProperty) => ({ nodeName: tree.name, modelName: model.name, propertyName: userProperty.name })),
      Option.getOrUndefined,
    )
    const sheetWithUserField =
      userProperty === undefined ? sheetWithDefaults : sheetWithDefaults.addPropertyField(userProperty)

    const dateProperty = pipe(
      model.properties,
      Array.findFirst((property) => property.type === 'date' || property.type === 'datetime'),
      Option.map((dateProperty) => ({ nodeName: tree.name, modelName: model.name, propertyName: dateProperty.name })),
      Option.getOrUndefined,
    )
    const sheetWithUserAndDateField =
      dateProperty === undefined ? sheetWithUserField : sheetWithUserField.addPropertyField(dateProperty)

    setForm((oldValue) =>
      updateMetricFormValue(oldValue, {
        sheet: sheetWithUserAndDateField.config,
        // ベースオブジェクトが変更された時に、前のmeasureの値が残っていると不具合の原因になるのでリセットする
        measure: {
          type: 'kpiPreset',
          function: 'count',
          property: {
            nodeName: tree.name,
            modelName: tree.modelName,
            propertyName: 'id',
          },
        },
        date: dateProperty === undefined ? undefined : { property: dateProperty },
        user: userProperty === undefined ? undefined : { joinAs: 'id', property: userProperty },
      }),
    )
    setFormModified(true)
  }

  const onChangeTree = ({
    picked,
    unPicked,
  }: {
    picked?: Array<{ node: ViewConfigTreeNode; referenceToProperty: ReferenceToPropertyForAddChildNodeMutation }>
    unPicked?: Array<ViewConfigTreeNode | undefined>
  }): void => {
    const addedSheet = (picked ?? []).reduce((accumulator, x) => {
      const result = accumulator.addViewConfigChildNode(x.node, x.referenceToProperty)
      if (!result.success) {
        return accumulator
      }
      const model = modelSearcher.searchModel(result.newNode.modelName)
      if (model === undefined) {
        // modelは見つかるはず
        return result.data
      }
      // modelのデフォルトプロパティを追加する
      return result.data.addDefaultPropertyFields(result.newNode.name, model)
    }, sheet)
    const newSheet = (unPicked ?? []).reduce((accumulator, x) => accumulator.removeChildNode(x?.name ?? ''), addedSheet)
    setCurrentSheet(newSheet.config)
    setFormModified(true)
  }

  return (
    <StepContentWrapper
      targetStep={1}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      kpiSaveing={kpiSaveing}
      kpiSave={kpiSave}
      view={view}
    >
      <KpiFormRootNodeFormItem modelName={currentSheet.tree?.modelName} setModel={onChangeModel} />
      <KpiFormChildNodeFormItem tree={currentSheet.tree} updateTree={onChangeTree} />
    </StepContentWrapper>
  )
}

const StepContent2 = ({
  currentStep,
  setCurrentStep,
  kpiSaveing,
  kpiSave,
  view,
}: {
  currentStep: KpiFormStep
  setCurrentStep: (value: number) => void
  /** KPIの保存ローディング状態（ショートカットキーでの保存状態を共有する）*/
  kpiSaveing: boolean
  /** KPIの保存実行 */
  kpiSave: () => void
  view: ViewFieldsFragment
}) => {
  const [currentSheet, setCurrentSheet] = [useCurrentSheet(), useSetSheetConfig()]
  const sheet = new ViewConfigSheetValueObject(currentSheet)
  const [form, setForm] = useRecoilState(kpiFormAtom)
  const setFormModified = useSetRecoilState(kpiFormModifiedAtom)

  const onChangeMeasure = (measure: Partial<ViewConfigKpiMeasure>): void => {
    setForm((oldValue) => updateMetricFormValue(oldValue, { measure }))
    setFormModified(true)
  }

  const onChangeMeasureProperty = (property: NodePropertyName | undefined): void => {
    if (property === undefined) {
      setForm((oldValue) =>
        updateMetricFormValue(oldValue, { measure: { ...oldValue.metricFormValue?.measure, property } }),
      )
    } else {
      // 選択されたプロパティを詳細項目に自動追加
      const newSheetConfig = sheet.addPropertyField(property).config
      setForm((oldValue) =>
        updateMetricFormValue(oldValue, {
          sheet: newSheetConfig,
          measure: { ...oldValue.metricFormValue?.measure, property },
        }),
      )
    }
    setFormModified(true)
  }

  const onChangeJoinAs = (joinAs: 'id' | 'email' | 'name' | undefined): void => {
    setForm((oldValue) => updateMetricFormValue(oldValue, { user: { joinAs } }))
    setFormModified(true)
  }

  const onChangeUserProperty = (property: NodePropertyName | undefined): void => {
    if (property === undefined) {
      setForm((oldValue) => updateMetricFormValue(oldValue, { user: { ...oldValue.metricFormValue?.user, property } }))
    } else {
      // 選択されたプロパティを詳細項目に自動追加
      const newSheetConfig = sheet.addPropertyField(property).config
      setForm((oldValue) =>
        updateMetricFormValue(oldValue, {
          sheet: newSheetConfig,
          user: { ...oldValue.metricFormValue?.user, property },
        }),
      )
    }
    setFormModified(true)
  }

  const onChangeDate = (date: { property: NodePropertyName } | undefined): void => {
    if (date === undefined) {
      setForm((oldValue) => updateMetricFormValue(oldValue, { date }))
    } else {
      // 選択されたプロパティを詳細項目に自動追加
      const newSheetConfig = sheet.addPropertyField(date.property).config
      setForm((oldValue) => updateMetricFormValue(oldValue, { sheet: newSheetConfig, date }))
    }
    setFormModified(true)
  }

  return (
    <StepContentWrapper
      targetStep={2}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      kpiSaveing={kpiSaveing}
      kpiSave={kpiSave}
      view={view}
    >
      <KpiFormMeasureFormItem
        measure={form.metricFormValue?.measure}
        setMeasure={onChangeMeasure}
        setMeasureProperty={onChangeMeasureProperty}
      />
      <KpiFormUserFormItem
        joinAs={form.metricFormValue?.user?.joinAs}
        setJoinAs={onChangeJoinAs}
        userProperty={form.metricFormValue?.user?.property}
        setUserProperty={onChangeUserProperty}
      />
      <KpiFormDateFormItem date={form.metricFormValue?.date} setDate={onChangeDate} />
      <KpiFormFilterTreeFormItem currentSheet={currentSheet} setCurrentSheet={setCurrentSheet} />
    </StepContentWrapper>
  )
}

const StepContent3 = ({
  currentStep,
  setCurrentStep,
  kpiSaveing,
  kpiSave,
  view,
}: {
  currentStep: KpiFormStep
  setCurrentStep: (value: number) => void
  /** KPIの保存ローディング状態（ショートカットキーでの保存状態を共有する）*/
  kpiSaveing: boolean
  /** KPIの保存実行 */
  kpiSave: () => void
  view: ViewFieldsFragment
}) => {
  const [currentSheet, setCurrentSheet] = [useCurrentSheet(), useSetSheetConfig()]
  const { kpi, setKpi } = useKpi()
  const [form, setForm] = useRecoilState(kpiFormAtom)
  const setFormModified = useSetRecoilState(kpiFormModifiedAtom)

  const onChangeCurrentSheet = (currentSheet: ViewConfigSheet): void => {
    setCurrentSheet(currentSheet)
    setFormModified(true)
  }

  const onChangeDimensionFieldsVisibility = (checked: boolean): void => {
    setForm((oldValue) => ({
      ...oldValue,
      options: { dimensionFieldsVisibility: checked },
    }))
    setFormModified(true)
  }

  const onChangeFilterTree = (filterTree: ViewConfigFilterNode | undefined): void => {
    setForm((oldValue) => ({
      ...oldValue,
      sheetExtraSetting: { filterTree },
    }))
  }

  const onChangeKpi = (kpi: Partial<ViewKpiAppearance>): void => {
    setKpi(kpi)
    setFormModified(true)
  }

  return (
    <StepContentWrapper
      targetStep={3}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      kpiSaveing={kpiSaveing}
      kpiSave={kpiSave}
      view={view}
    >
      <KpiFormSheetFieldsFormItem currentSheet={currentSheet} setCurrentSheet={onChangeCurrentSheet} />
      <KpiFormSheetFieldsFormAddDimeisions
        checked={form.options?.dimensionFieldsVisibility}
        setChecked={onChangeDimensionFieldsVisibility}
      />
      <KpiFormExtraFilterTreeFormItem
        currentSheetTree={currentSheet.tree}
        filterTree={form.sheetExtraSetting?.filterTree}
        setFilterTree={onChangeFilterTree}
      />
      <KpiFormDetailFormItems kpi={kpi} setKpi={onChangeKpi} />
    </StepContentWrapper>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function StepContentWrapper({
  targetStep,
  children,
  currentStep,
  setCurrentStep,
  kpiSaveing,
  kpiSave,
  view,
}: {
  targetStep: KpiFormStep
  children: ReactElement[]
  currentStep: KpiFormStep
  setCurrentStep: (value: number) => void
  /** KPIの保存ローディング状態（ショートカットキーでの保存状態を共有する）*/
  kpiSaveing: boolean
  /** KPIの保存実行 */
  kpiSave: () => void
  view: ViewFieldsFragment
}) {
  const canUpdate = useCanForView('update', view)
  const { views } = useRecoilValue(viewsContextAtom)
  const { checkStepEnabled } = useStepStatus()
  const [formModified, setFormModified] = useRecoilState(kpiFormModifiedAtom)
  const [, setForm] = useRecoilState(kpiFormAtom)

  return (
    <div style={{ display: targetStep === currentStep ? 'block' : 'none' }}>
      {children}
      <Row justify="space-between">
        <Col>
          {formModified && (
            <ButtonWithTooltip
              tooltipTitle={t(`権限がないのでKPI設定の変更を破棄できません`)}
              showTooltip={!canUpdate}
              disabled={
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                !formModified || !canUpdate
              }
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                // viewAtomはタブ切り替え時にしか更新されないので、最新のconfigを参照するために
                // viewsContext内のviewsを使用する
                const currentView = views.find((x) => x.id === view.id)
                if (currentView?.config.type !== 'kpi') {
                  // ありえないはず
                  return
                }
                const kpiForm = generateInitialKpiForm({ viewName: currentView.name, config: currentView.config })
                // ViewFilterTreeForm は内部状態を持ち cacheKey 変化でしか reset しないため、
                // 破棄時に filterFormItemCacheKey を bump してフィルタUIの内部状態も初期状態に戻す
                setForm({ ...kpiForm, filterFormItemCacheKey: new Date().toISOString() })
                setFormModified(false)
              }}
            >
              {t('KPI設定変更を破棄')}
            </ButtonWithTooltip>
          )}
        </Col>
        <Col>
          <Space>
            <>
              {currentStep > 0 && (
                <Button
                  type="default"
                  onClick={() => {
                    setCurrentStep(currentStep - 1)
                  }}
                  data-e2e={`kpi-form-step-${currentStep}-back-button`}
                >
                  {t(`前へ`)}
                </Button>
              )}
              {currentStep === MAX_STEP ? (
                <Button disabled>{t(`次へ`)}</Button>
              ) : (
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={!checkStepEnabled(currentStep + 1)}
                  onClick={() => {
                    setCurrentStep(currentStep + 1)
                  }}
                  data-e2e={`kpi-form-step-${currentStep}-next-button`}
                >
                  {t(`次へ`)}
                </Button>
              )}
            </>
            <KpiFormSaveButton disabled={!formModified} kpiSaveing={kpiSaveing} kpiSave={kpiSave} view={view} />
          </Space>
        </Col>
      </Row>
    </div>
  )
}
